/**
 * Measurement tool
 * - Click first point, move to second point, click again
 * - Shows distance line with label in meters (one decimal)
 * - Creates a measurement annotation in stage-store
 * - Escape: cancel
 */

import { Vec2, distanceBetween, snapToGrid } from '@/canvas/coordinates';
import { ToolHandler, ToolContext } from './tool-handler';
import { useEditorStore } from '@/core/stores/editor-store';
import { useStageStore } from '@/core/stores/stage-store';
import { useHistoryStore } from '@/core/stores/history-store';
import * as defaults from '@/stage/defaults';

interface MeasureToolState {
  startPoint: Vec2 | null;
  endPoint: Vec2 | null;
  previewPos: Vec2 | null;
  measurements: Array<{
    startPoint: Vec2;
    endPoint: Vec2;
    distance: number;
  }>;
}

const state: MeasureToolState = {
  startPoint: null,
  endPoint: null,
  previewPos: null,
  measurements: [],
};

export function getMeasureToolHandler(): ToolHandler {
  return {
    onMouseDown(ctx: ToolContext) {
      if (ctx.button !== 0) return; // Only left-click

      const { snapToGrid: shouldSnap, gridSize } = useEditorStore.getState();

      // Determine position with optional snap
      const pos = shouldSnap ? snapToGrid(ctx.worldPos, gridSize) : ctx.worldPos;

      if (state.startPoint === null) {
        // Set start point
        state.startPoint = pos;
        state.endPoint = null;
        state.previewPos = pos;
      } else {
        // Set end point and create measurement
        state.endPoint = pos;

        // Calculate distance
        const distance = distanceBetween(state.startPoint, state.endPoint);

        // Store measurement
        state.measurements.push({
          startPoint: state.startPoint,
          endPoint: state.endPoint,
          distance,
        });

        // Create annotation in stage store
        const stageState = useStageStore.getState();
        const historyState = useHistoryStore.getState();
        const editorState = useEditorStore.getState();
        const { layout } = stageState;

        if (layout) {
          const midpoint = {
            x: (state.startPoint.x + state.endPoint.x) / 2,
            y: (state.startPoint.y + state.endPoint.y) / 2,
            z: 0,
          };

          const annotation = defaults.createMeasurementAnnotation(
            [state.startPoint, state.endPoint],
            midpoint
          );
          annotation.value = Math.round(distance * 10) / 10; // Round to 1 decimal place

          // Push pre-mutation state to history BEFORE mutating
          historyState.pushState(layout);

          stageState.addAnnotation(annotation);
          editorState.setDirty(true);
        }

        // Reset for next measurement
        state.startPoint = null;
        state.endPoint = null;
        state.previewPos = null;
      }
    },

    onMouseMove(ctx: ToolContext) {
      const { snapToGrid: shouldSnap, gridSize } = useEditorStore.getState();

      state.previewPos = shouldSnap ? snapToGrid(ctx.worldPos, gridSize) : ctx.worldPos;
    },

    onKeyDown(key: string) {
      if (key === 'Escape') {
        // Cancel current measurement
        state.startPoint = null;
        state.endPoint = null;
        state.previewPos = null;
      }
    },

    getCursor() {
      return 'crosshair';
    },

    renderPreview(ctx2d: CanvasRenderingContext2D, zoom: number) {
      // Draw all completed measurements
      ctx2d.strokeStyle = 'rgba(100, 200, 100, 0.8)';
      ctx2d.lineWidth = 0.05;

      for (const measurement of state.measurements) {
        ctx2d.beginPath();
        ctx2d.moveTo(measurement.startPoint.x, measurement.startPoint.y);
        ctx2d.lineTo(measurement.endPoint.x, measurement.endPoint.y);
        ctx2d.stroke();

        // Draw distance label at midpoint
        const midpoint = {
          x: (measurement.startPoint.x + measurement.endPoint.x) / 2,
          y: (measurement.startPoint.y + measurement.endPoint.y) / 2,
        };

        ctx2d.fillStyle = 'rgba(100, 200, 100, 1)';
        ctx2d.font = `${14 / zoom}px Arial`;
        ctx2d.textAlign = 'center';
        ctx2d.textBaseline = 'middle';
        ctx2d.fillText(`${measurement.distance.toFixed(1)}m`, midpoint.x, midpoint.y);
      }

      // Draw current measurement in progress
      if (state.startPoint && state.previewPos) {
        ctx2d.strokeStyle = 'rgba(100, 150, 255, 0.8)';
        ctx2d.lineWidth = 0.05;
        ctx2d.setLineDash([0.2, 0.2]);

        ctx2d.beginPath();
        ctx2d.moveTo(state.startPoint.x, state.startPoint.y);
        ctx2d.lineTo(state.previewPos.x, state.previewPos.y);
        ctx2d.stroke();

        ctx2d.setLineDash([]);

        // Draw start point
        ctx2d.fillStyle = 'rgba(100, 150, 255, 0.8)';
        ctx2d.beginPath();
        ctx2d.arc(state.startPoint.x, state.startPoint.y, 0.15, 0, Math.PI * 2);
        ctx2d.fill();

        // Draw distance label
        const distance = distanceBetween(state.startPoint, state.previewPos);
        const midpoint = {
          x: (state.startPoint.x + state.previewPos.x) / 2,
          y: (state.startPoint.y + state.previewPos.y) / 2,
        };

        ctx2d.fillStyle = 'rgba(100, 150, 255, 1)';
        ctx2d.font = `${14 / zoom}px Arial`;
        ctx2d.textAlign = 'center';
        ctx2d.textBaseline = 'middle';
        ctx2d.fillText(`${distance.toFixed(1)}m`, midpoint.x, midpoint.y);
      }
    },
  };
}
